import React from "react";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useSelectedProductOption as useSelectedProductOptionCode } from "../../../hooks/useSelectedProductOption";
import { SidebarPanel } from "../sidebar-panel";
import { MenuIds, useMenus } from "../../../hooks/useMenus";
import { SidebarOptions } from "./sidebar-options-list";
import { useLabels } from "../../../hooks/useLabels";

import { labels } from "../../bottom-menu-panels/product-options/labels";
import { AlternativeIcon } from "../../alternative-icon/alternative-icon";

import { useCart } from "../../../hooks/useCart";
import { useQuery } from "@tanstack/react-query";
import { useProductVariantCode } from "../../../hooks/useProductVariantCode";
import { useAppContext } from "../../../hooks/useAppContext";
import { SvgIcon } from "../../svg-icon";

function useProductOptionPrice(optionPrice: number) {
	const cart = useCart();
	const { client } = useAppContext();
	const productVariantCode = useProductVariantCode();

	return useQuery({
		queryKey: ["useProductoptionPrice", productVariantCode, cart, optionPrice],
		queryFn: async () => {
			if (!cart || !productVariantCode) return null;

			/*
			 * We don't have a price object in our model,
			 * So we perform a new request to get the price formating rules
			 * Normally this will be a cached request
			 * To be sure a cached price object is not changed, we clone it and change the price for the option
			 */
			const price = (
				await client.getPriceAsync(cart, productVariantCode, 0, undefined, undefined)
			).clone();

			price.priceWithDiscount = optionPrice;
			return price.toString();
		},
		enabled: !!cart && !!productVariantCode,
		meta: {
			logToServer: true,
			errorPrefix: "Error loading product option price",
		},
	});
}

function SidebarProductOption({
	type,
	option,
	group,
	showPrice,
	isActive,
	onClick,
}: {
	type: "grid" | "list";
	option: eXpress.core.IProductOption;
	group: eXpress.core.IProductOptionGroup;
	isActive: boolean;
	onClick: () => void;
	showPrice: boolean;
}) {
	const optionSpecificLabel = labels.specificNoOptions(group.code);
	const [[noNameLabel, specificNoOptionLabel]] = useLabels([
		labels.noNameDefault,
		optionSpecificLabel,
	]);

	const { data: price } = useProductOptionPrice(option.price);

	const name = option.name || specificNoOptionLabel || noNameLabel || <span />;
	const icon = option.icon;

	return (
		<SidebarOptions.Item
			type={type}
			icon={
				icon ? (
					<AlternativeIcon
						svgUrl={icon.svgUrl}
						color={icon.color}
						overlayColor={icon.overlayColor}
					/>
				) : undefined
			}
			title={name}
			subTitle={showPrice && price ? price : undefined}
			isActive={isActive}
			onClick={onClick}
		/>
	);
}

export function SidebarProductOptions({ group }: { group: eXpress.core.IProductOptionGroup }) {
	const dashboard = useDashboardContext();
	const { createErrorHandler } = useErrorHandling();
	const productOptionCode = useSelectedProductOptionCode();
	const showPrice = group.productOptions.values.some((option) => option.price);

	const {
		actions: { setMenu },
	} = useMenus();

	const type = group.productOptions.values.every((option) => option.icon) ? "grid" : "list";

	return (
		<>
			<SidebarPanel.SubTitle icon={group.icon ? <SvgIcon svgUrl={group.icon} /> : undefined}>
				{group.name}
			</SidebarPanel.SubTitle>
			<SidebarOptions.List type={type}>
				{group.productOptions.values.map((option) => {
					return (
						<SidebarProductOption
							key={option.name}
							type={type}
							group={group}
							option={option}
							showPrice={showPrice}
							isActive={option.code === productOptionCode}
							onClick={() => {
								// Mark product options menu as active
								setMenu(MenuIds.ProductOptions);
								dashboard
									.changeProductOptionAsync(
										option && option.code ? option.code : undefined,
									)
									.catch(createErrorHandler());
							}}
						/>
					);
				})}
			</SidebarOptions.List>
		</>
	);
}
